import { useEffect, useRef, useState } from "react";
import "./css/brand_loader.css";

const DEFAULT_SIZE = { fullscreen: 12, overlay: 10, inline: 10 };

// Anti-flicker: stays invisible for loads under `delay`ms (most loads), so a
// quick fetch never flashes anything. Once shown, it stays at least
// `minDuration`ms so the dots themselves do not blink. `size` is the dot size in px.
export default function BrandLoader({
  show = true,
  variant = "fullscreen",
  label,
  size,
  delay = 600,
  minDuration = 500,
  className,
}) {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (show) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (!visible && !showTimerRef.current) {
        showTimerRef.current = setTimeout(() => {
          setVisible(true);
          shownAtRef.current = Date.now();
          showTimerRef.current = null;
        }, delay);
      }
    } else {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (visible) {
        const elapsed = Date.now() - (shownAtRef.current || 0);
        const remaining = Math.max(0, minDuration - elapsed);
        hideTimerRef.current = setTimeout(() => {
          setVisible(false);
          shownAtRef.current = null;
          hideTimerRef.current = null;
        }, remaining);
      }
    }
  }, [show, delay, minDuration, visible]);

  useEffect(
    () => () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    []
  );

  if (!visible) return null;

  const markSize = size || DEFAULT_SIZE[variant] || DEFAULT_SIZE.inline;

  return (
    <div
      className={`rl-loader rl-loader--${variant}${className ? ` ${className}` : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className="rl-loader-dots" style={{ "--dot": `${markSize}px` }} aria-hidden="true">
        <span /><span /><span />
      </div>
      {label && <div className="rl-loader-label">{label}</div>}
    </div>
  );
}
